import { useState, useEffect } from 'react';
import { usePldConfig, useUpdatePldConfig } from '../../../hooks/usePld';
import { Settings, Save, AlertTriangle, Bell, Clock, Shield } from 'lucide-react';

export default function PldConfig() {
  const { data: config, isLoading, error } = usePldConfig();
  const updateMutation = useUpdatePldConfig();
  
  const [formData, setFormData] = useState({
    isEnabled: true,
    autoEscalationHours: 48,
    notifyOnHighSeverity: true,
    notifyOnCriticalSeverity: true,
    blockOnCritical: false,
    dailyReportEnabled: true,
    dailyReportTime: '08:00',
    retentionDays: 365,
    complianceEmail: '',
    escalationEmail: '',
  });

  useEffect(() => {
    if (config) {
      setFormData({
        isEnabled: config.isEnabled ?? true,
        autoEscalationHours: config.autoEscalationHours ?? 48,
        notifyOnHighSeverity: config.notifyOnHighSeverity ?? true,
        notifyOnCriticalSeverity: config.notifyOnCriticalSeverity ?? true,
        blockOnCritical: config.blockOnCritical ?? false,
        dailyReportEnabled: config.dailyReportEnabled ?? true,
        dailyReportTime: config.dailyReportTime ?? '08:00',
        retentionDays: config.retentionDays ?? 365,
        complianceEmail: config.complianceEmail ?? '',
        escalationEmail: config.escalationEmail ?? '',
      });
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync(formData);
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="h-64 bg-surface-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-7 h-7 text-primary-400" />
          Configurações PLD
        </h1>
        <p className="text-surface-400 mt-1">
          Configure as regras de prevenção à lavagem de dinheiro do seu tenant
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Erro ao carregar configurações
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Settings */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            Configurações Gerais
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <p className="font-medium">Módulo PLD Ativo</p>
                <p className="text-sm text-surface-400">Ativar ou desativar o monitoramento PLD</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <p className="font-medium">Bloquear Transações Críticas</p>
                <p className="text-sm text-surface-400">Bloquear automaticamente transações com alerta crítico</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.blockOnCritical}
                  onChange={(e) => setFormData(prev => ({ ...prev, blockOnCritical: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Retenção de Dados (dias)</label>
              <input
                type="number"
                value={formData.retentionDays}
                onChange={(e) => setFormData(prev => ({ ...prev, retentionDays: Number(e.target.value) }))}
                min={90}
                max={3650}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-sm text-surface-400 mt-1">Mínimo recomendado: 365 dias (exigência BACEN)</p>
            </div>
          </div>
        </div>

        {/* Escalation Settings */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Escalação Automática
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tempo para Escalação (horas)</label>
              <input
                type="number"
                value={formData.autoEscalationHours}
                onChange={(e) => setFormData(prev => ({ ...prev, autoEscalationHours: Number(e.target.value) }))}
                min={1}
                max={168}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-sm text-surface-400 mt-1">
                Alertas não analisados serão escalados automaticamente após este período
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">E-mail para Escalação</label>
              <input
                type="email"
                value={formData.escalationEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, escalationEmail: e.target.value }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="compliance-senior@empresa.com"
              />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-400" />
            Notificações
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <p className="font-medium">Notificar Alertas de Alta Severidade</p>
                <p className="text-sm text-surface-400">Enviar e-mail quando alertas HIGH forem gerados</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifyOnHighSeverity}
                  onChange={(e) => setFormData(prev => ({ ...prev, notifyOnHighSeverity: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <p className="font-medium">Notificar Alertas Críticos</p>
                <p className="text-sm text-surface-400">Enviar e-mail imediato para alertas CRITICAL</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifyOnCriticalSeverity}
                  onChange={(e) => setFormData(prev => ({ ...prev, notifyOnCriticalSeverity: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">E-mail do Compliance</label>
              <input
                type="email"
                value={formData.complianceEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, complianceEmail: e.target.value }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="compliance@empresa.com"
              />
            </div>
          </div>
        </div>

        {/* Daily Report Settings */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            Relatório Diário
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <p className="font-medium">Enviar Relatório Diário</p>
                <p className="text-sm text-surface-400">Resumo diário de alertas e atividades</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.dailyReportEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, dailyReportEnabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {formData.dailyReportEnabled && (
              <div>
                <label className="block text-sm font-medium mb-2">Horário do Relatório</label>
                <input
                  type="time"
                  value={formData.dailyReportTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, dailyReportTime: e.target.value }))}
                  className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {updateMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>

        {updateMutation.isSuccess && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400">
            Configurações salvas com sucesso!
          </div>
        )}

        {updateMutation.isError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
            Erro ao salvar configurações. Tente novamente.
          </div>
        )}
      </form>
    </div>
  );
}
